/**
 * dashboardAlerts.js — what needs attention today
 * ================================================
 * Usability review of 23 October 2026, item 10: *"Add an alerts strip at the
 * top. List what needs attention today. These are scattered or buried at the
 * bottom today: stale prices; unposted transactions; notices waiting for
 * review; upcoming deadlines."*
 *
 * Every check is listed whether or not it found anything, so a clean strip
 * reads as "the checks ran and passed" rather than "nothing was checked"
 * (CLAUDE.md §0.32). A check whose source did not load says so instead of
 * claiming zero.
 */
import { positionModel } from "./DashboardPositions";
import { maturityEvents } from "./portfolioDashboardData";
import { dealReportPath } from "./links";
import { day } from "./format";

const STALE_DAYS = 7;

/** Positions whose price is old, or that should have one and have none. */
export function stalePrices(nav) {
  if (!nav?.positions) return null;
  return positionModel(nav).positions.filter((p) => {
    const priced = p.last_price !== null && p.last_price !== undefined;
    if (priced) return Boolean(p.price_source) && p.price_age_days > STALE_DAYS;
    // A listed share with no price is a gap; a note, a preferred, a PPLN or a
    // private interest at cost is a ruling, and is not flagged (§0.30).
    return p.instrument_type === "Equity" && !p.is_private && /unpriced/i.test(p.valuation_method || "");
  });
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "SHF Holdings, Inc. (2), PPCB IPO (1)": who the rows belong to, most first. */
export function companiesWithCounts(items) {
  const counts = new Map();
  for (const item of items) {
    const name = item.company || "not tied to a deal";
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, n]) => `${name} (${n})`)
    .join(", ");
}

export function dashboardAlerts({ nav, treasury, notices, review }) {
  const alerts = [];

  const stale = stalePrices(nav);
  alerts.push(stale === null
    ? { key: "prices", tone: "unknown", count: null, label: "Prices not checked", detail: "The valuation has not loaded yet.", to: "/securities" }
    : {
      key: "prices",
      tone: stale.length ? "warn" : "ok",
      count: stale.length,
      label: stale.length ? plural(stale.length, "stale or missing price", "stale or missing prices") : "Prices current",
      detail: stale.length
        ? stale.slice(0, 6).map((p) => `${p.name || p.ticker}${p.price_date ? ` (${day(p.price_date)})` : " (no price)"}`).join(", ")
          + (stale.length > 6 ? ` and ${stale.length - 6} more` : "")
        : `Every listed holding has a price from the last ${STALE_DAYS} days.`,
      to: stale.length ? "/securities?needs=price" : "/securities",
    });

  // The review list counts settled bank rows only; a failed or pending payment
  // is not money that moved (§0.19). The treasury figure is the fallback.
  const unposted = review && Number.isFinite(Number(review.bank_rows_waiting ?? NaN))
    ? Number(review.bank_rows_waiting)
    : treasury ? Number(treasury.unposted_count ?? NaN) : NaN;
  alerts.push(!Number.isFinite(unposted)
    ? { key: "unposted", tone: "unknown", count: null, label: "Bank feed not checked", detail: "The treasury summary has not loaded.", to: "/bank-feed" }
    : {
      key: "unposted",
      tone: unposted ? "warn" : "ok",
      count: unposted,
      label: unposted ? plural(unposted, "unposted bank transaction", "unposted bank transactions") : "Bank feed posted",
      detail: unposted ? "Waiting to be booked to the ledger on Bank Feed." : "Every bank transaction is booked.",
      to: "/bank-feed",
    });

  // A notice sent more than once is waiting too, until somebody answers it (D-278).
  const pending = Array.isArray(notices?.pending)
    ? [...notices.pending, ...(Array.isArray(notices.sent_again) ? notices.sent_again : [])]
    : null;
  alerts.push(pending === null
    ? { key: "notices", tone: "unknown", count: null, label: "Notices not checked", detail: "The notice queue has not loaded.", to: "/notice-review" }
    : {
      key: "notices",
      tone: pending.length ? "warn" : "ok",
      count: pending.length,
      label: pending.length ? plural(pending.length, "notice awaiting review", "notices awaiting review") : "No notices waiting",
      detail: pending.length ? "Found by the nightly mail and Drive watch; confirm or reject on Approvals." : "The nightly watch has nothing waiting.",
      to: "/approvals",
    });

  // SHF Holdings' ITG sales of 20 and 21 October 2026 sat a week unposted and
  // the strip had no check for broker rows (D-278). Since 27 October a clean
  // row posts by itself, so what is counted is a decision (D-281); a sale
  // waiting on a conversion is counted with the notices.
  const brokerRows = Array.isArray(review?.items) ? review.items.filter((i) => i.kind === "broker_row") : null;
  const posting = Number(review?.broker_rows_posting) || 0;
  alerts.push(brokerRows === null
    ? { key: "broker", tone: "unknown", count: null, label: "Broker rows not checked", detail: "The review list has not loaded.", to: "/trade-blotter" }
    : {
      key: "broker",
      tone: brokerRows.length ? "warn" : "ok",
      count: brokerRows.length,
      label: brokerRows.length
        ? plural(brokerRows.length, "broker row needs a decision", "broker rows need a decision")
        : "Broker rows posting by themselves",
      detail: brokerRows.length
        ? companiesWithCounts(brokerRows)
        : posting
          ? `${plural(posting, "clean row posts", "clean rows post")} on the next run; nothing needs a decision.`
          : "Every broker row is posted or ruled out.",
      to: brokerRows.length ? "/approvals" : "/trade-blotter",
    });

  const events = nav ? maturityEvents(nav) : null;
  alerts.push(events === null
    ? { key: "deadlines", tone: "unknown", count: null, label: "Deadlines not checked", detail: "The valuation has not loaded yet.", to: "/positions" }
    : {
      key: "deadlines",
      tone: events.length ? "info" : "ok",
      count: events.length,
      label: events.length ? plural(events.length, "note maturity in 90 days", "note maturities in 90 days") : "No maturities in 90 days",
      detail: events.length
        ? events.slice(0, 4).map((e) => `${e.name}: ${day(e.date)}`).join(" · ")
        : "No note on the book falls due in the next 90 days.",
      to: events.length && events[0].deal_id ? dealReportPath(events[0].deal_id) : "/positions",
    });

  return alerts;
}

/** The figure for "open items": everything the checks found. */
export const openItemCount = (alerts) => alerts.reduce((n, a) => n + (a.tone === "warn" || a.tone === "info" ? a.count || 0 : 0), 0);
